import {
	type AttachmentFile,
	type AttachmentFileType,
	ContractSuretyType,
	Dtos,
	ExtractionStatus,
	type FileId,
	FileType,
	type SuretyAccountId,
	type SuretyQuoteId,
} from "@inrev/common";
import { useFetchAdminContractExtractions } from "@portal/api/bond-request/admin";
import { fileViewerControl } from "@portal/api/files";
import { adminQuoteAttachmentUrlQueryKey, useFetchQuoteAttachments } from "@portal/api/quote";
import { useFeatureFlag } from "@portal/hooks/useFeatureFlag";
import { type FC, useCallback, useMemo, useState } from "react";
import { useGetFileUrl } from "../../../api";
import { type PDFViewerControl } from "../../patterns/pdf-viewer";
import { PDFViewerModal } from "../../ui/PDFViewerModal";
import { KeyDocumentCard, type KeyDocumentFile } from "./KeyDocumentCard";
import { useKeyDocumentUpload } from "./useKeyDocumentUpload";

type KeyDocumentConfig = { type: AttachmentFileType; label: string; emptyMessage: string };

const CONTRACT_DOCUMENT: KeyDocumentConfig = {
	type: FileType.contract,
	label: "Contract",
	emptyMessage: "Upload the contract",
};

const RFP_DOCUMENT: KeyDocumentConfig = {
	type: FileType.bid_package,
	label: "RFP / Bid Package",
	emptyMessage: "Upload the RFP / bid package",
};

const BID_RESULTS_DOCUMENT: KeyDocumentConfig = {
	type: FileType.bid_results,
	label: "Bid Results",
	emptyMessage: "Upload the bid results",
};

const getKeyDocuments = (contractSuretyType: ContractSuretyType): KeyDocumentConfig[] => [
	...(contractSuretyType !== ContractSuretyType.bid ? [CONTRACT_DOCUMENT] : []),
	contractSuretyType === ContractSuretyType.bid_to_final ? BID_RESULTS_DOCUMENT : RFP_DOCUMENT,
];

const EMPTY_ATTACHMENTS: AttachmentFile[] = [];

type ViewerState =
	| { kind: "attachment"; attachmentId: string; attachmentFileId: FileId; header: string }
	| { kind: "summary"; fileId: FileId; header: string };

export type AdminBondRequestKeyDocumentsProps = {
	accountId: SuretyAccountId;
	quoteId: SuretyQuoteId;
	contractSuretyType: ContractSuretyType;
	/** Blocks upload and removal once the request is past the point of changing its documents. */
	readOnly?: boolean;
};

export const AdminBondRequestKeyDocuments: FC<AdminBondRequestKeyDocumentsProps> = ({
	accountId,
	quoteId,
	contractSuretyType,
	readOnly = false,
}) => {
	const { attachments } = useFetchQuoteAttachments(accountId, quoteId);
	const { upload, remove, isUploading, isDeleting } = useKeyDocumentUpload({
		accountId,
		quoteId,
	});

	const isExtractionEnabled = useFeatureFlag("bond_request_extraction");
	const { extractions } = useFetchAdminContractExtractions({
		accountId,
		quoteId,
		enabled: isExtractionEnabled,
	});

	const { getFileUrl } = useGetFileUrl();
	const [viewer, setViewer] = useState<ViewerState | undefined>();

	const extractionByFileId = useMemo(() => {
		const map = new Map<string, Dtos.ContractExtraction.Get.Response>();
		for (const extraction of extractions ?? []) {
			map.set(extraction.fileId, extraction);
		}
		return map;
	}, [extractions]);

	const viewerControl = useMemo<PDFViewerControl | undefined>(() => {
		if (!viewer) return undefined;
		if (viewer.kind === "summary") {
			return fileViewerControl(viewer.fileId);
		}
		return {
			fetchUrl: `/v2/admin/surety/quotes/${quoteId}/attachments/${viewer.attachmentId}`,
			queryKey: adminQuoteAttachmentUrlQueryKey(viewer.attachmentId),
			fileId: viewer.attachmentFileId,
		};
	}, [viewer, quoteId]);

	const downloadAttachment = useCallback(
		(attachmentId: string) =>
			getFileUrl({
				baseUrl: `/v2/admin/surety/quotes/${quoteId}/attachments/${attachmentId}`,
				queryKey: adminQuoteAttachmentUrlQueryKey(attachmentId),
				asDownload: true,
			}),
		[getFileUrl, quoteId],
	);

	const toKeyDocumentFile = useCallback(
		(attachment: AttachmentFile): KeyDocumentFile => {
			const fileName = `${attachment.file.name}.${attachment.file.extension}`;
			const extraction = extractionByFileId.get(attachment.file.id);
			const summaryPdfFileId = extraction?.pdfFileId;
			const isPdf = attachment.file.extension.toLowerCase() === "pdf";

			return {
				id: attachment.id,
				name: fileName,
				extension: attachment.file.extension,
				deleting: isDeleting(attachment.id),
				classificationStatus: attachment.file.classificationStatus,
				typesConfirmedByAdmin: attachment.file.typesConfirmedByAdmin,
				onPreview: isPdf
					? () =>
							setViewer({
								kind: "attachment",
								attachmentId: attachment.id,
								attachmentFileId: attachment.file.id,
								header: attachment.file.name,
							})
					: undefined,
				onDownload: () => downloadAttachment(attachment.id),
				onViewSummary: summaryPdfFileId
					? () =>
							setViewer({
								kind: "summary",
								fileId: summaryPdfFileId,
								header: "View Document Summary",
							})
					: undefined,
				summaryLoading: extraction?.status === ExtractionStatus.Pending && !summaryPdfFileId,
				onRemove: readOnly ? undefined : () => remove(attachment.id),
			};
		},
		[extractionByFileId, isDeleting, downloadAttachment, readOnly, remove],
	);

	return (
		<>
			<div
				className="grid grid-cols-1 gap-4 lg:grid-cols-2"
				data-testid="admin-bond-request-key-documents"
			>
				{getKeyDocuments(contractSuretyType).map(({ type, label, emptyMessage }) => {
					const matching = (attachments ?? EMPTY_ATTACHMENTS).filter((a) =>
						a.file.types.includes(type),
					);

					return (
						<KeyDocumentCard
							key={type}
							testId={`key-document-${type}`}
							label={label}
							emptyMessage={emptyMessage}
							files={matching.map(toKeyDocumentFile)}
							uploading={isUploading(type)}
							onUpload={readOnly ? undefined : (files) => upload(files, type)}
						/>
					);
				})}
			</div>
			<PDFViewerModal
				control={viewerControl}
				onClose={() => setViewer(undefined)}
				header={viewer?.header}
				trackingId={
					viewer?.kind === "summary"
						? "key-documents-contract-summary-viewer"
						: "key-documents-attachment-viewer"
				}
			/>
		</>
	);
};
